import React, { useState, useEffect } from 'react';
import { Check, ChevronRight, Trophy, Target, ClipboardCheck, HelpCircle, Heart } from 'lucide-react';

const WeeklyCheckInPrompt = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({
    win_shared: false,
    goals_set_count: 0,
    review_done: false,
    help_needed: false,
    encouragement_sent: false
  });
  const [totalPoints, setTotalPoints] = useState(0);
  
  // Step 1: Share Win
  const [winText, setWinText] = useState('');
  
  // Step 2: Set Commitments
  const [selectedCategory, setSelectedCategory] = useState('');
  const [goals, setGoals] = useState(['', '', '']);
  const [templates, setTemplates] = useState([]);
  
  // Step 3: Review Last Week (placeholder for now)
  const [lastWeekReview, setLastWeekReview] = useState([]);
  
  // Step 4: Help Needed
  const [helpTypes, setHelpTypes] = useState([]);
  
  // Step 5: Encouragement
  const [encouragementMessage, setEncouragementMessage] = useState('');
  const [encouragementTemplates, setEncouragementTemplates] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Load check-in status
    loadCheckInStatus();
    loadEncouragementTemplates();
  }, []);

  const loadCheckInStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/member/accountability/v2/checkin/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.has_partner) {
          setCompletedSteps(data.completed_steps);
          setTotalPoints(data.points_awarded);
        }
      }
    } catch (error) {
      console.error('Error loading check-in status:', error);
    }
  };

  const loadEncouragementTemplates = async () => {
    const templates = [
      "You've got this! Keep pushing forward! 💪",
      "I believe in you! You're doing amazing! 🌟",
      "Let's crush these goals together! 🚀",
      "You're making incredible progress! Keep it up! ⭐",
      "I'm here to support you every step of the way! 🤝",
      "Your dedication is inspiring! Let's go! 🔥",
      "We're in this together! You're not alone! 💙",
      "Every small step counts! You're doing great! 👏"
    ];
    setEncouragementTemplates(templates);
  };

  const loadCommitmentTemplates = async (category) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/member/accountability/v2/commitment-templates/${category}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleStep1Submit = async () => {
    if (!winText.trim()) {
      alert('Please share your win!');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/member/accountability/v2/checkin/share-win`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ win_text: winText })
      });

      if (response.ok) {
        const data = await response.json();
        setTotalPoints(data.total_weekly_points);
        setCompletedSteps(prev => ({ ...prev, win_shared: true }));
        setCurrentStep(1);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save win');
      }
    } catch (error) {
      console.error('Error sharing win:', error);
      alert('Failed to share win');
    }
  };

  const handleStep2Submit = async () => {
    const filledGoals = goals.filter(g => g.trim());
    
    if (filledGoals.length === 0) {
      alert('Please set at least one commitment!');
      return;
    }

    if (!selectedCategory) {
      alert('Please select a category!');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/member/accountability/v2/checkin/set-commitments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          goals: filledGoals,
          category: selectedCategory
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTotalPoints(data.total_weekly_points);
        setCompletedSteps(prev => ({ ...prev, goals_set_count: filledGoals.length }));
        setCurrentStep(2);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to set commitments');
      }
    } catch (error) {
      console.error('Error setting commitments:', error);
      alert('Failed to set commitments');
    }
  };

  const handleStep3Skip = () => {
    // For now, skip review (can be implemented later)
    setCurrentStep(3);
  };

  const handleStep4Submit = async () => {
    if (helpTypes.length === 0) {
      alert('Please select at least one help type!');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/member/accountability/v2/checkin/help-needed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ help_types: helpTypes })
      });

      if (response.ok) {
        setCompletedSteps(prev => ({ ...prev, help_needed: true }));
        setCurrentStep(4);
      }
    } catch (error) {
      console.error('Error saving help request:', error);
    }
  };

  const handleStep5Submit = async () => {
    if (!encouragementMessage.trim()) {
      alert('Please write an encouragement message!');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/member/accountability/v2/checkin/send-encouragement`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: encouragementMessage })
      });

      if (response.ok) {
        setCompletedSteps(prev => ({ ...prev, encouragement_sent: true }));
        onComplete?.();
        onClose();
        alert('Weekly check-in complete! 🎉');
      }
    } catch (error) {
      console.error('Error sending encouragement:', error);
    }
  };

  const categories = [
    { value: 'business', label: '💼 Business', icon: '💼' },
    { value: 'fitness', label: '💪 Fitness', icon: '💪' },
    { value: 'school', label: '📚 School', icon: '📚' },
    { value: 'self_improvement', label: '🌟 Self-Improvement', icon: '🌟' },
    { value: 'mixed_productivity', label: '⚡ Mixed Productivity', icon: '⚡' }
  ];

  const helpOptions = [
    { value: 'accountability', label: 'Accountability', icon: '✓' },
    { value: 'strategy', label: 'Strategy', icon: '📋' },
    { value: 'time', label: 'Time Management', icon: '⏰' },
    { value: 'motivation', label: 'Motivation', icon: '🔥' },
    { value: 'resources', label: 'Resources', icon: '📚' }
  ];

  const steps = [
    {
      title: "Share Your Win",
      icon: Trophy,
      description: "What's one win from your week?",
      component: (
        <div className="space-y-4">
          <textarea
            value={winText}
            onChange={(e) => setWinText(e.target.value)}
            placeholder="Share your biggest win from this week..."
            className="w-full p-3 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            rows={4}
          />
          <button
            onClick={handleStep1Submit}
            disabled={completedSteps.win_shared}
            className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {completedSteps.win_shared ? <Check className="w-5 h-5" /> : null}
            {completedSteps.win_shared ? 'Win Shared! +1 Point' : 'Share Win (+1 Point)'}
          </button>
        </div>
      )
    },
    {
      title: "Set 3 Commitments",
      icon: Target,
      description: "What are your 3 goals for this week?",
      component: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Choose Category:</label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => {
                    setSelectedCategory(cat.value);
                    loadCommitmentTemplates(cat.value);
                  }}
                  className={`p-3 border rounded-lg text-left ${
                    selectedCategory === cat.value
                      ? 'border-amber-600 bg-amber-50'
                      : 'border-gray-300 hover:border-amber-400'
                  }`}
                >
                  <div className="font-medium">{cat.label}</div>
                </button>
              ))}
            </div>
          </div>

          {selectedCategory && templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">Quick Templates (tap to use):</label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {templates.map((template, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const emptyIndex = goals.findIndex(g => !g.trim());
                      if (emptyIndex !== -1) {
                        const newGoals = [...goals];
                        newGoals[emptyIndex] = template;
                        setGoals(newGoals);
                      }
                    }}
                    className="w-full text-left p-2 text-sm border border-gray-200 rounded hover:bg-amber-50 hover:border-amber-300"
                  >
                    {template}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Your 3 Commitments:</label>
            {goals.map((goal, idx) => (
              <input
                key={idx}
                value={goal}
                onChange={(e) => {
                  const newGoals = [...goals];
                  newGoals[idx] = e.target.value;
                  setGoals(newGoals);
                }}
                placeholder={`Commitment ${idx + 1}`}
                className="w-full p-2 mb-2 border border-amber-300 rounded focus:ring-2 focus:ring-amber-500"
              />
            ))}
          </div>

          <button
            onClick={handleStep2Submit}
            disabled={completedSteps.goals_set_count >= 3}
            className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {completedSteps.goals_set_count >= 3 ? <Check className="w-5 h-5" /> : null}
            {completedSteps.goals_set_count >= 3 ? 'Commitments Set! +3 Points' : 'Set Commitments (+3 Points)'}
          </button>
        </div>
      )
    },
    {
      title: "Review Last Week",
      icon: ClipboardCheck,
      description: "How did you do on last week's goals?",
      component: (
        <div className="space-y-4">
          <p className="text-gray-600">Review feature coming soon. Skip for now.</p>
          <button
            onClick={handleStep3Skip}
            className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700"
          >
            Continue
          </button>
        </div>
      )
    },
    {
      title: "What Help Do You Need?",
      icon: HelpCircle,
      description: "Select what you need support with",
      component: (
        <div className="space-y-4">
          <div className="space-y-2">
            {helpOptions.map(option => (
              <button
                key={option.value}
                onClick={() => {
                  if (helpTypes.includes(option.value)) {
                    setHelpTypes(helpTypes.filter(h => h !== option.value));
                  } else {
                    setHelpTypes([...helpTypes, option.value]);
                  }
                }}
                className={`w-full p-3 border rounded-lg text-left flex items-center gap-3 ${
                  helpTypes.includes(option.value)
                    ? 'border-amber-600 bg-amber-50'
                    : 'border-gray-300 hover:border-amber-400'
                }`}
              >
                <span>{option.icon}</span>
                <span className="font-medium">{option.label}</span>
                {helpTypes.includes(option.value) && <Check className="w-5 h-5 ml-auto text-amber-600" />}
              </button>
            ))}
          </div>
          <button
            onClick={handleStep4Submit}
            disabled={completedSteps.help_needed}
            className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {completedSteps.help_needed ? <Check className="w-5 h-5" /> : null}
            {completedSteps.help_needed ? 'Help Request Sent! +1 Point' : 'Send Help Request (+1 Point)'}
          </button>
        </div>
      )
    },
    {
      title: "Send Encouragement",
      icon: Heart,
      description: "Send a supportive message to your partner",
      component: (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Quick Messages:</label>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {encouragementTemplates.map((template, idx) => (
                <button
                  key={idx}
                  onClick={() => setEncouragementMessage(template)}
                  className="w-full text-left p-2 text-sm border border-gray-200 rounded hover:bg-amber-50 hover:border-amber-300"
                >
                  {template}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={encouragementMessage}
            onChange={(e) => setEncouragementMessage(e.target.value)}
            placeholder="Write your encouragement message..."
            className="w-full p-3 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500"
            rows={3}
          />
          <button
            onClick={handleStep5Submit}
            disabled={completedSteps.encouragement_sent}
            className="w-full bg-amber-600 text-white py-3 rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {completedSteps.encouragement_sent ? <Check className="w-5 h-5" /> : null}
            {completedSteps.encouragement_sent ? 'Encouragement Sent! +1 Point' : 'Send Encouragement (+1 Point)'}
          </button>
        </div>
      )
    }
  ];

  const CurrentStepIcon = steps[currentStep].icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Weekly Check-In</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              ✕
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm">
              Progress: {currentStep + 1} / {steps.length}
            </div>
            <div className="text-sm">
              Points Earned: {totalPoints} / 10
            </div>
          </div>
          <div className="mt-4 flex gap-1">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 flex-1 rounded ${
                  idx <= currentStep ? 'bg-white' : 'bg-amber-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <CurrentStepIcon className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold">{steps[currentStep].title}</h3>
              <p className="text-gray-600">{steps[currentStep].description}</p>
            </div>
          </div>

          {steps[currentStep].component}
        </div>

        {/* Navigation */}
        <div className="p-6 border-t flex justify-between">
          <button
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <button
            onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
            disabled={currentStep === steps.length - 1}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeeklyCheckInPrompt;
